import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AddCardPanel } from '../components/AddCardPanel';
import { customerApi } from '../customer_api';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export function PaymentMethodsPage() {
  const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingCard, setAddingCard] = useState(false);

  const refresh = useCallback(() => {
    customerApi<{ payment_methods: PaymentMethod[] }>('/api/customer/payment-methods')
      .then((d) => setMethods(d.payment_methods))
      .catch(() => setMethods([]));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setDefault = async (pmId: string) => {
    setBusy(pmId);
    setError(null);
    try {
      await customerApi(`/api/customer/payment-methods/${pmId}/default`, { method: 'PATCH' });
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (pmId: string) => {
    setBusy(pmId);
    setError(null);
    try {
      await customerApi(`/api/customer/payment-methods/${pmId}`, { method: 'DELETE' });
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const startAddCard = () => {
    setError(null);
    setAddingCard(true);
  };

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/account"
          style={{
            fontSize: '0.85rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            textDecoration: 'none',
          }}
        >
          ← Back
        </Link>
        <h1
          style={{
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '1rem 0 1.5rem',
          }}
        >
          Payment methods
        </h1>

        {error && (
          <p
            role="alert"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              background: 'oklch(0.55 0.15 30 / 0.08)',
              color: 'oklch(0.42 0.15 30)',
              fontSize: '0.9rem',
            }}
          >
            {error}
          </p>
        )}

        {methods === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {methods !== null && methods.length === 0 && (
          <p
            data-testid="payment-methods-empty"
            style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))', fontSize: '0.9rem' }}
          >
            No saved cards yet. Cards are saved automatically when you pay for a booking.
          </p>
        )}

        {methods !== null &&
          methods.map((pm) => (
            <div
              key={pm.id}
              data-testid={`pm-${pm.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.85rem',
                padding: '1rem 1.25rem',
                borderRadius: '12px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
                background: 'var(--surface, white)',
                marginBottom: '0.75rem',
              }}
            >
              <CreditCard size={20} style={{ color: 'var(--primary, oklch(0.43 0.18 263))' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                  {pm.brand} •••• {pm.last4}
                </div>
                <div style={{ fontSize: '0.8rem', opacity: 0.65 }}>
                  Expires {String(pm.expMonth).padStart(2, '0')}/{String(pm.expYear).slice(-2)}
                </div>
              </div>
              {pm.isDefault ? (
                <span
                  style={{
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '999px',
                    background: 'oklch(0.55 0.12 150 / 0.12)',
                    color: 'oklch(0.36 0.12 150)',
                  }}
                >
                  Default
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setDefault(pm.id)}
                  disabled={busy === pm.id}
                  data-testid={`pm-set-default-${pm.id}`}
                  style={{
                    fontSize: '0.8rem',
                    padding: '0.3rem 0.7rem',
                    borderRadius: '6px',
                    border: '1px solid var(--border, oklch(0.915 0.005 80))',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Make default
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(pm.id)}
                disabled={busy === pm.id}
                aria-label="Remove card"
                data-testid={`pm-remove-${pm.id}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--destructive, oklch(0.55 0.15 30))',
                  padding: '0.4rem',
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}

        {addingCard ? (
          <AddCardPanel
            onSaved={() => {
              setAddingCard(false);
              refresh();
            }}
            onCancel={() => setAddingCard(false)}
          />
        ) : (
          <button
            type="button"
            onClick={startAddCard}
            data-testid="add-card-cta"
            style={{
              marginTop: '0.5rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.7rem 1rem',
              borderRadius: '10px',
              border: '1px dashed var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Add a card
          </button>
        )}
      </div>
    </main>
  );
}
